(() => {
  const registry = window.NipponVinoWineAtlasRenderers = window.NipponVinoWineAtlasRenderers || {};
  const Base = registry.Base;
  if (!Base) return;

  const CURATED_REGION_IDS = new Set([
    'yamanashi',
    'nagano',
    'hokkaido',
    'yamagata',
    'iwate',
    'ibaraki',
    'niigata',
    'osaka',
    'okayama',
    'shimane',
  ]);

  const ISLAND_GROUPS = Object.freeze({
    hokkaido: new Set(['01']),
    shikoku: new Set(['36', '37', '38', '39']),
    kyushu: new Set(['40', '41', '42', '43', '44', '45', '46']),
  });

  class IsometricArchipelagoRenderer extends Base {
    mount(container, data, state = {}) {
      super.mount(container, data, state);
      this.render();
      this.update(this.matchingIds, state);
      this.setLevel(this.level, { animate: false });
    }

    getLayout() {
      const width = this.mapCanvas?.clientWidth || window.innerWidth;
      if (width <= 600) {
        return {
          name: 'mobile', width: 420, height: 310,
          frame: { x: 9, y: 22, width: 402, height: 252 },
          baseDepth: 3.2,
          extrusionX: 0.14,
          elevations: {
            japan: { wine: 5.5, shop: 11, hover: 4, selected: 21, selectedShopBonus: 3 },
            region: { wine: 3.2, shop: 6, hover: 2.8, selected: 14, selectedShopBonus: 2 },
            detail: { wine: 2.2, shop: 4, hover: 2, selected: 9.5, selectedShopBonus: 1.5 },
          },
        };
      }
      return {
        name: width <= 900 ? 'tablet' : 'desktop', width: 1200, height: 690,
        frame: { x: 64, y: 42, width: 1072, height: 566 },
        baseDepth: 4,
        extrusionX: 0.18,
        elevations: {
          japan: { wine: 7, shop: 13, hover: 5, selected: 24, selectedShopBonus: 3 },
          region: { wine: 3.5, shop: 6.5, hover: 3, selected: 15.5, selectedShopBonus: 2 },
          detail: { wine: 2.4, shop: 4.2, hover: 2, selected: 10.5, selectedShopBonus: 1.5 },
        },
      };
    }

    polygonArea(polygon) {
      const ring = polygon?.[0] || [];
      let area = 0;
      for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
        area += (Number(ring[previous]?.[0]) * Number(ring[index]?.[1]))
          - (Number(ring[index]?.[0]) * Number(ring[previous]?.[1]));
      }
      return Math.abs(area / 2);
    }

    prepareFeatures() {
      return this.features
        .filter((feature) => String(feature?.properties?.code || '').padStart(2, '0') !== '47')
        .map((feature) => {
          const polygons = feature.geometry?.type === 'Polygon'
            ? [feature.geometry.coordinates]
            : feature.geometry?.coordinates || [];
          const mainland = [...polygons].sort((a, b) => this.polygonArea(b) - this.polygonArea(a))[0];
          return {
            ...feature,
            geometry: { type: 'Polygon', coordinates: mainland || [] },
          };
        });
    }

    islandGroup(code) {
      if (ISLAND_GROUPS.hokkaido.has(code)) return 'hokkaido';
      if (ISLAND_GROUPS.shikoku.has(code)) return 'shikoku';
      if (ISLAND_GROUPS.kyushu.has(code)) return 'kyushu';
      return 'honshu';
    }

    createCompactProjector(features, frame, layoutName) {
      const mercator = ([longitude, latitude]) => {
        const clipped = Math.max(-85, Math.min(85, Number(latitude)));
        return [
          Number(longitude) * Math.PI / 180,
          -Math.log(Math.tan((Math.PI / 4) + ((clipped * Math.PI / 180) / 2))),
        ];
      };
      const rotation = (layoutName === 'mobile' ? 9 : 13) * Math.PI / 180;
      const tilt = layoutName === 'mobile' ? 0.64 : 0.57;
      const cosine = Math.cos(rotation);
      const sine = Math.sin(rotation);
      const affine = ([x, y]) => [
        (x * cosine) - (y * sine),
        ((x * sine) + (y * cosine)) * tilt,
      ];
      const rawByGroup = new Map();
      const rawEntries = [];
      features.forEach((feature) => {
        const code = String(feature.properties?.code || '').padStart(2, '0');
        const group = this.islandGroup(code);
        this.eachCoordinate(feature, (coordinate) => {
          const point = affine(mercator(coordinate));
          rawEntries.push({ code, group, point });
          if (!rawByGroup.has(group)) rawByGroup.set(group, []);
          rawByGroup.get(group).push(point);
        });
      });
      const allXs = rawEntries.map((entry) => entry.point[0]);
      const allYs = rawEntries.map((entry) => entry.point[1]);
      const spanX = Math.max(...allXs) - Math.min(...allXs);
      const spanY = Math.max(...allYs) - Math.min(...allYs);
      const centers = new Map([...rawByGroup].map(([group, points]) => [group, {
        x: (Math.min(...points.map((point) => point[0])) + Math.max(...points.map((point) => point[0]))) / 2,
        y: (Math.min(...points.map((point) => point[1])) + Math.max(...points.map((point) => point[1]))) / 2,
      }]));
      const transforms = layoutName === 'mobile'
        ? {
          honshu: { scale: 1, dx: 0, dy: 0 },
          hokkaido: { scale: 1.07, dx: -spanX * 0.032, dy: spanY * 0.068 },
          shikoku: { scale: 1.01, dx: spanX * 0.009, dy: -spanY * 0.014 },
          kyushu: { scale: 1, dx: spanX * 0.023, dy: -spanY * 0.022 },
        }
        : {
          honshu: { scale: 1, dx: 0, dy: 0 },
          hokkaido: { scale: 1.09, dx: -spanX * 0.038, dy: spanY * 0.074 },
          shikoku: { scale: 1.015, dx: spanX * 0.01, dy: -spanY * 0.016 },
          kyushu: { scale: 1, dx: spanX * 0.026, dy: -spanY * 0.025 },
        };
      const transformRaw = (point, code) => {
        const group = this.islandGroup(code);
        const transform = transforms[group];
        const center = centers.get(group);
        return [
          center.x + ((point[0] - center.x) * transform.scale) + transform.dx,
          center.y + ((point[1] - center.y) * transform.scale) + transform.dy,
        ];
      };
      const transformed = rawEntries.map((entry) => transformRaw(entry.point, entry.code));
      const xs = transformed.map((point) => point[0]);
      const ys = transformed.map((point) => point[1]);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const padding = layoutName === 'mobile' ? 16 : 22;
      const content = {
        x: frame.x + padding,
        y: frame.y + padding,
        width: Math.max(1, frame.width - (padding * 2)),
        height: Math.max(1, frame.height - (padding * 2)),
      };
      const scale = Math.min(
        content.width / Math.max(maxX - minX, 0.0001),
        content.height / Math.max(maxY - minY, 0.0001)
      );
      const offsetX = content.x + ((content.width - ((maxX - minX) * scale)) / 2);
      const offsetY = content.y + ((content.height - ((maxY - minY) * scale)) / 2);
      return (coordinate, code = '') => {
        const transformedPoint = transformRaw(affine(mercator(coordinate)), String(code).padStart(2, '0'));
        return [
          offsetX + ((transformedPoint[0] - minX) * scale),
          offsetY + ((transformedPoint[1] - minY) * scale),
        ];
      };
    }

    projectorForFeature(feature) {
      const code = String(feature?.properties?.code || '').padStart(2, '0');
      return (coordinate) => this.projector(coordinate, code);
    }

    featureSidePath(feature, projector, depth) {
      const polygons = feature.geometry?.type === 'Polygon'
        ? [feature.geometry.coordinates]
        : feature.geometry?.coordinates || [];
      return polygons.map((polygon) => {
        const ring = polygon?.[0] || [];
        const projected = ring.map(projector);
        const signedArea = projected.reduce((area, point, index) => {
          const next = projected[(index + 1) % projected.length];
          return area + ((point[0] * next[1]) - (next[0] * point[1]));
        }, 0);
        const extrusionX = depth * (this.layout?.extrusionX || 0);
        return projected.slice(0, -1).map(([x1, y1], index) => {
          const [x2, y2] = projected[index + 1] || projected[0];
          const cross = ((x2 - x1) * depth) - ((y2 - y1) * extrusionX);
          const facesViewer = signedArea > 0 ? cross < 0 : cross > 0;
          if (!facesViewer || Math.abs(cross) < 0.08) return '';
          return `M${x1.toFixed(2)},${y1.toFixed(2)}L${x2.toFixed(2)},${y2.toFixed(2)}L${(x2 + extrusionX).toFixed(2)},${(y2 + depth).toFixed(2)}L${(x1 + extrusionX).toFixed(2)},${(y1 + depth).toFixed(2)}Z`;
        }).join('');
      }).join('');
    }

    elevationProfile() {
      return this.layout?.elevations?.[this.level] || this.layout?.elevations?.japan || {
        wine: 0, shop: 0, hover: 0, selected: 0, selectedShopBonus: 0,
      };
    }

    elevationFor(regionId, state = 'rest') {
      const profile = this.elevationProfile();
      const region = this.controller.regionById.get(regionId);
      const shop = (region?.availableWines || 0) > 0;
      const rest = shop ? profile.shop : profile.wine;
      if (state === 'hover') return rest + profile.hover;
      if (state === 'selected') return profile.selected + (shop ? profile.selectedShopBonus : 0);
      return rest;
    }

    projectedElevation(elevation) {
      return {
        x: elevation * (this.layout?.extrusionX || 0),
        y: elevation,
      };
    }

    render() {
      const layout = this.getLayout();
      this.layout = layout;
      this.layoutName = layout.name;
      this.controller.dataset.atlasLayout = layout.name;
      this.controller.dataset.mapRenderer = 'isometric-a';
      this.geometryById.clear();
      this.featureById = new Map();
      this.surfaceById = new Map();
      this.preparedFeatures = this.prepareFeatures();
      this.projector = this.createCompactProjector(this.preparedFeatures, layout.frame, layout.name);

      const svg = this.createSvg(
        layout.width,
        layout.height,
        'Isometrischer Archipel der japanischen Weinregionen',
        'Kompakte SVG-Karte der vier japanischen Hauptinseln. Zehn kuratierte Weinregionen können mit Maus, Touch, Enter oder Leertaste geöffnet werden; die übrigen Präfekturen dienen ausschließlich der Orientierung.'
      );
      svg.classList.add('wine-atlas-isometric-map');
      svg.dataset.atlasLevel = this.level;

      const defs = this.createElement('defs');
      const filter = this.createElement('filter', {
        id: `atlas-ground-shadow-${this.controller.id}`, x: '-20%', y: '-30%', width: '150%', height: '180%',
      });
      filter.appendChild(this.createElement('feGaussianBlur', { stdDeviation: layout.name === 'mobile' ? 2.6 : 4.2 }));
      defs.appendChild(filter);
      svg.appendChild(defs);

      const ground = this.createElement('g', { class: 'isometric-ground', 'aria-hidden': 'true' });
      const geography = this.createElement('g', { class: 'isometric-geography' });
      this.preparedFeatures.forEach((feature) => {
        const item = this.regionForFeature(feature);
        const interactive = Boolean(item.region && CURATED_REGION_IDS.has(item.id));
        const featureProjector = this.projectorForFeature(feature);
        const d = this.featurePath(feature, featureProjector);
        const bounds = this.featureBounds(feature, featureProjector);
        if (interactive) {
          this.featureById.set(item.id, feature);
          if (bounds) this.geometryById.set(item.id, bounds);
        }

        ground.appendChild(this.createElement('path', { class: 'isometric-ground-piece', d }));
        const group = this.createElement('g', {
          class: `isometric-prefecture ${interactive ? 'atlas-curated atlas-profiled' : 'atlas-context'}`,
          'data-prefecture-code': item.code,
        });
        const shadow = this.createElement('path', { class: 'isometric-prefecture-shadow', d, 'aria-hidden': 'true' });
        const lift = this.createElement('g', { class: 'isometric-prefecture-lift' });
        const baseSide = this.createElement('path', {
          class: 'isometric-prefecture-side isometric-prefecture-side--base',
          d: this.featureSidePath(feature, featureProjector, layout.baseDepth),
          'aria-hidden': 'true',
        });
        const hoverSide = this.createElement('path', {
          class: 'isometric-prefecture-side isometric-prefecture-side--hover',
          d: this.featureSidePath(feature, featureProjector, layout.baseDepth),
          'aria-hidden': 'true',
        });
        const selectedSide = this.createElement('path', {
          class: 'isometric-prefecture-side isometric-prefecture-side--selected',
          d: this.featureSidePath(feature, featureProjector, layout.baseDepth),
          'aria-hidden': 'true',
        });
        lift.append(
          baseSide,
          hoverSide,
          selectedSide,
          this.createElement('path', { class: 'isometric-prefecture-top', d, 'aria-hidden': 'true' })
        );
        if (interactive) {
          this.surfaceById.set(item.id, {
            group, feature, projector: featureProjector, baseSide, hoverSide, selectedSide,
          });
          lift.appendChild(this.createElement('path', { class: 'isometric-prefecture-hit', d, 'aria-hidden': 'true' }));
          this.bindInteractive(group, feature);
        } else {
          group.setAttribute('aria-hidden', 'true');
        }
        group.append(shadow, lift);
        geography.appendChild(group);
      });

      this.placesLayer = this.renderPlaces(layout);
      this.selectionPlacesLayer = this.createElement('g', { class: 'isometric-selection-places', 'aria-hidden': 'true' });
      this.shopLabel = this.renderShopLabel(layout);
      svg.append(ground, geography, this.placesLayer, this.selectionPlacesLayer, this.shopLabel);

      this.container.replaceChildren(svg);
      this.container.setAttribute('aria-busy', 'false');
      this.svg = svg;
      this.originalViewBox = { x: 0, y: 0, width: layout.width, height: layout.height };
      this.japanViewBox = { ...this.originalViewBox };
      this.bindPointerHitResolution();
      this.syncElevations();
    }

    bindPointerHitResolution() {
      const resolveHit = (event) => {
        if (!event.detail || !event.target.closest?.('.isometric-prefecture.atlas-curated')) return;
        try {
          const matrix = this.svg.getScreenCTM()?.inverse();
          if (!matrix) return;
          const point = this.svg.createSVGPoint();
          point.x = event.clientX;
          point.y = event.clientY;
          const local = point.matrixTransform(matrix);
          let nearest = { id: '', distance: Number.POSITIVE_INFINITY };
          this.geometryById.forEach((bounds, id) => {
            const dx = local.x - (bounds.x + (bounds.width / 2));
            const dy = local.y - (bounds.y + (bounds.height / 2));
            const distance = (dx * dx) + (dy * dy);
            if (distance < nearest.distance) nearest = { id, distance };
          });
          const feature = this.featureById.get(nearest.id);
          if (!feature) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.selectFeature(feature, { focusDetail: false });
        } catch (error) {
          // The region's own click handler remains available as a safe fallback.
        }
      };
      this.svg.addEventListener('click', resolveHit, true);
      this.listeners.push([this.svg, 'click', resolveHit]);
    }

    regionCode(regionId) {
      return String(this.controller.regionById.get(regionId)?.prefCode || '').padStart(2, '0');
    }

    createPlace(place, className = '') {
      const code = this.regionCode(place.regionId);
      const [x, y] = this.projector([Number(place.point.longitude), Number(place.point.latitude)], code);
      const group = this.createElement('g', {
        class: `isometric-place${place.primary ? ' isometric-place--primary' : ''}${className ? ` ${className}` : ''}`,
        'data-region-ids': place.regionId,
      });
      group.append(
        this.createElement('line', {
          class: 'isometric-place-leader', x1: x, y1: y,
          x2: x + (place.dx * 0.72), y2: y + (place.dy * 0.72),
        }),
        this.createElement('circle', { class: 'isometric-place-ring', cx: x, cy: y, r: place.primary ? 6.5 : 5 }),
        this.createElement('circle', { class: 'isometric-place-point', cx: x, cy: y, r: place.primary ? 3.4 : 2.6 })
      );
      const label = this.createElement('text', {
        class: 'isometric-place-label', x: x + place.dx, y: y + place.dy,
        'text-anchor': place.dx < 0 ? 'end' : 'start',
      });
      label.textContent = place.name;
      group.appendChild(label);
      return group;
    }

    renderPlaces(layout) {
      const layer = this.createElement('g', { class: 'isometric-places', 'aria-label': 'Orientierungsstädte' });
      const byId = new Map(this.regions.map((region) => [region.id, region]));
      this.basePlaceKeys = new Set();
      const places = [
        { name: 'Sapporo', point: byId.get('hokkaido')?.atlas?.referenceCity, regionId: 'hokkaido', dx: 14, dy: -15 },
        { name: 'Nagano', point: byId.get('nagano')?.atlas?.wineHub, regionId: 'nagano', dx: -14, dy: -16 },
        { name: 'Kōfu', point: byId.get('yamanashi')?.atlas?.wineHub, regionId: 'yamanashi', dx: -16, dy: 21 },
        { name: 'Tokio', point: byId.get('yamanashi')?.atlas?.referenceCity, regionId: 'yamanashi', dx: 17, dy: -13, primary: true },
        { name: 'Osaka', point: byId.get('osaka')?.atlas?.referenceCity, regionId: 'osaka', dx: -16, dy: 21 },
      ].filter((place) => Number.isFinite(Number(place.point?.longitude)) && Number.isFinite(Number(place.point?.latitude)));

      if (layout.name === 'mobile') {
        const mobileOffsets = {
          Sapporo: [11, -15], Nagano: [8, -25], 'Kōfu': [-4, 25], Tokio: [24, 15], Osaka: [-24, 19],
        };
        places.forEach((place) => [place.dx, place.dy] = mobileOffsets[place.name] || [place.dx, place.dy]);
      }
      places.forEach((place) => {
        this.basePlaceKeys.add(`${Number(place.point.longitude).toFixed(4)}:${Number(place.point.latitude).toFixed(4)}`);
        layer.appendChild(this.createPlace(place));
      });
      return layer;
    }

    renderShopLabel(layout) {
      const yamanashi = this.geometryById.get('yamanashi');
      const layer = this.createElement('g', {
        class: 'isometric-shop-label',
        'data-shop-label-region': 'yamanashi',
        'aria-hidden': 'true',
      });
      if (!yamanashi) return layer;
      const anchor = {
        x: yamanashi.x + (yamanashi.width / 2),
        y: yamanashi.y + (yamanashi.height / 2),
      };
      const width = layout.name === 'mobile' ? 102 : 118;
      const height = layout.name === 'mobile' ? 34 : 38;
      const x = anchor.x - width - (layout.name === 'mobile' ? 48 : 18);
      const y = anchor.y - height - (layout.name === 'mobile' ? 16 : 20);
      layer.append(
        this.createElement('line', {
          class: 'isometric-shop-label__leader', x1: anchor.x, y1: anchor.y,
          x2: x + width, y2: y + (height * 0.72),
        }),
        this.createElement('rect', {
          class: 'isometric-shop-label__plaque', x, y, width, height, rx: 2,
        }),
        this.createElement('circle', {
          class: 'isometric-shop-label__signal', cx: x + 9, cy: y + height - 10, r: 3,
        })
      );
      const name = this.createElement('text', {
        class: 'isometric-shop-label__name', x: x + 8, y: y + 14,
      });
      name.textContent = 'Yamanashi';
      const badge = this.createElement('text', {
        class: 'isometric-shop-label__badge', x: x + 16, y: y + height - 7,
      });
      badge.textContent = 'BEI NIPPON VINO';
      layer.append(name, badge);
      return layer;
    }

    renderSelectionPlaces() {
      if (!this.selectionPlacesLayer) return;
      this.selectionPlacesLayer.replaceChildren();
      const region = this.controller.regionById.get(this.selectedId);
      if (!region?.atlas) return;
      const points = [region.atlas.referenceCity, region.atlas.wineHub, ...(region.atlas.additionalPoints || [])];
      const seen = new Set();
      points.forEach((point, index) => {
        if (!Number.isFinite(Number(point?.longitude)) || !Number.isFinite(Number(point?.latitude))) return;
        const key = `${Number(point.longitude).toFixed(4)}:${Number(point.latitude).toFixed(4)}`;
        if (seen.has(key) || this.basePlaceKeys?.has(key)) return;
        seen.add(key);
        const place = {
          name: point.name,
          point,
          regionId: region.id,
          dx: Number(point.labelDx) || (index % 2 ? -13 : 13),
          dy: Number(point.labelDy) || (index % 2 ? 16 : -14),
        };
        this.selectionPlacesLayer.appendChild(this.createPlace(place, 'isometric-place--selection'));
      });
    }

    update(matchingIds, state = {}) {
      super.update(matchingIds, state);
      this.svg?.querySelectorAll('.isometric-prefecture.atlas-curated').forEach((element) => {
        const id = element.dataset.atlasRegion;
        const selected = id === this.selectedId;
        const region = this.controller.regionById.get(id);
        element.classList.toggle('atlas-match', this.matchingIds.has(id));
        element.classList.toggle('atlas-available', (region?.availableWines || 0) > 0);
        element.classList.toggle('atlas-selected', selected);
        element.classList.toggle('atlas-filtered-out', !this.matchingIds.has(id));
        element.setAttribute('aria-pressed', String(selected));
        if (selected) element.parentNode?.appendChild(element);
      });
      this.svg?.classList.toggle('has-selection', Boolean(this.selectedId));
      this.svg?.querySelectorAll('[data-region-ids]').forEach((place) => {
        place.classList.toggle('is-current', Boolean(this.selectedId) && place.dataset.regionIds === this.selectedId);
      });
      this.shopLabel?.classList.toggle('is-current', this.selectedId === 'yamanashi');
      this.renderSelectionPlaces();
      this.syncElevations();
    }

    syncElevations() {
      if (!this.svg || !this.layout) return;
      this.surfaceById?.forEach((surface, regionId) => {
        const rest = this.elevationFor(regionId, 'rest');
        const hover = this.elevationFor(regionId, 'hover');
        const selected = this.elevationFor(regionId, 'selected');
        const restOffset = this.projectedElevation(rest);
        const hoverOffset = this.projectedElevation(hover);
        const selectedOffset = this.projectedElevation(selected);
        surface.group.style.setProperty('--isometric-rest-x', `${restOffset.x}px`);
        surface.group.style.setProperty('--isometric-rest-y', `${restOffset.y}px`);
        surface.group.style.setProperty('--isometric-hover-x', `${hoverOffset.x}px`);
        surface.group.style.setProperty('--isometric-hover-y', `${hoverOffset.y}px`);
        surface.group.style.setProperty('--isometric-selected-x', `${selectedOffset.x}px`);
        surface.group.style.setProperty('--isometric-selected-y', `${selectedOffset.y}px`);
        surface.group.style.setProperty('--isometric-shadow-x', `${Math.max(1.2, this.layout.extrusionX * 8)}px`);
        surface.baseSide.setAttribute(
          'd', this.featureSidePath(surface.feature, surface.projector, this.layout.baseDepth + rest)
        );
        surface.hoverSide.setAttribute(
          'd', this.featureSidePath(surface.feature, surface.projector, this.layout.baseDepth + hover)
        );
        surface.selectedSide.setAttribute(
          'd', this.featureSidePath(surface.feature, surface.projector, this.layout.baseDepth + selected)
        );
      });

      this.svg.querySelectorAll('[data-region-ids]').forEach((place) => {
        const regionId = place.dataset.regionIds;
        const state = regionId === this.selectedId ? 'selected' : 'rest';
        const offset = this.projectedElevation(this.elevationFor(regionId, state));
        place.style.setProperty('--isometric-place-x', `${offset.x}px`);
        place.style.setProperty('--isometric-place-y', `${offset.y}px`);
      });

      if (this.shopLabel) {
        const shopState = this.selectedId === 'yamanashi' ? 'selected' : 'rest';
        const shopOffset = this.projectedElevation(this.elevationFor('yamanashi', shopState));
        this.shopLabel.style.setProperty('--isometric-shop-x', `${shopOffset.x}px`);
        this.shopLabel.style.setProperty('--isometric-shop-y', `${shopOffset.y}px`);
      }
    }

    getSelectedContextBounds() {
      const selected = this.geometryById.get(this.selectedId);
      if (!selected) return null;
      const region = this.controller.regionById.get(this.selectedId);
      const code = this.regionCode(this.selectedId);
      const places = [region?.atlas?.referenceCity, region?.atlas?.wineHub, ...(region?.atlas?.additionalPoints || [])]
        .filter((point) => Number.isFinite(Number(point?.longitude)) && Number.isFinite(Number(point?.latitude)))
        .map((point) => {
          const [x, y] = this.projector([Number(point.longitude), Number(point.latitude)], code);
          return { x: x - 4, y: y - 4, width: 8, height: 8 };
        });
      return this.unionBounds([selected, ...places]);
    }

    setLevel(level, options = {}) {
      this.level = ['japan', 'region', 'detail'].includes(level) ? level : 'japan';
      const context = this.getSelectedContextBounds();
      const target = this.level === 'japan' || !context
        ? this.originalViewBox
        : this.fitBounds(context, this.level === 'detail' ? 1.9 : 3.1);
      this.svg?.setAttribute('data-atlas-level', this.level);
      this.syncElevations();
      this.animateViewBox(target, options.animate !== false);
    }

    resize() {
      const next = this.getLayout().name;
      if (next === this.layoutName) return;
      const container = this.container;
      const data = this.data;
      const state = { selectedId: this.selectedId, level: this.level, matchingIds: this.matchingIds };
      this.destroy();
      super.mount(container, data, state);
      this.render();
      this.update(this.matchingIds, state);
      this.setLevel(this.level, { animate: false });
    }
  }

  registry['isometric-a'] = IsometricArchipelagoRenderer;
})();
